<template>
  <v-menu
    v-model="showMenu"
    offset="12"
    width="280"
  >
    <template #activator="{ props }">
      <v-list-item
        v-if="mobileMenuMode"
        v-bind="props"
        :prepend-icon="mdiAccountCircle"
        :append-icon="mdiMenuDown"
        :title="name"
        :subtitle="formattedCustomerName"
        class="hoverable-locale-item"
        rounded="lg"
      />
      
      <v-btn
        v-else
        v-bind="props"
        class="h-75 d-none d-sm-flex is-clickable"
        size="small"
        rounded
      >
        <v-tooltip
          activator="parent"
          location="bottom"
        >
          <span>{{ tooltip }}</span>
        </v-tooltip>

        <div class="d-none d-md-flex flex-column align-end">
          <div class="account-name">
            {{ name }}
          </div>
          <div
            v-if="formattedCustomerName"
            class="text-caption text-weight-regular"
          >
            {{ formattedCustomerName }}
          </div>
        </div>
        <v-icon
          size="x-large"
          class="ml-0 ml-md-3"
          :icon="mdiAccountCircle"
          color="base-white"
        />
      </v-btn>
    </template>
  
    <crv-app-account-menu-content
      :name="name"
      :label="accountLabel"
      :is-switch-customer-page="isSwitchCustomerPage"
      :allow-switch-customer="allowSwitchCustomer"
      :customer-name="customerName"
      :locations="locations"
      :locations-label="locationsLabel"
      :logout-label="logoutLabel"
      :my-crv-label="myCrvLabel"
      :faq-label="faqLabel"
      :market-country-code="marketCountryCode"
      :is-my-crv="isMyCrv"
      :select-customer-text="selectCustomerText"
      :select-customer-route="selectCustomerRoute"
      :select-all-locations-label="selectAllLocationsLabel"
      :show-my-crv-and-faq="showMyCrvAndFaq"
      :tooltip-customer-switcher="tooltipCustomerSwitcher"
      :before-navigate-to-customer-switcher="beforeNavigateToCustomerSwitcher"
      @logout="handleLogout"
      @close="handleCloseAccountMenu"
      @switch-customer="handleSwitchCustomer"
      @click-location="handleClickLocation"
    /> 
  </v-menu>
</template>


<script>
import { mdiMenuDown, mdiAccountCircle } from '@mdi/js'
import CrvAppAccountMenuContent from '@v3c/components/CrvAppAccountMenuContent.vue'
import storageHelper from '@v3c/security/storageHelper'


export default {
    name: 'CrvAppAccountMenu',

    components: {
        CrvAppAccountMenuContent,
    },

    props: {
        name: {
            type: String,
            default: undefined,
        },

        allowSwitchCustomer: {
            type: Boolean,
            default: true,
        },

        accountLabel: {
            type: String,
            default: '',
        },

        customerName: {
            type: String,
            default: undefined,
        },

        customerNumber: {
            type: String,
            default: '',
        },

        selectCustomerText: {
            type: String,
            default: '$t("crvAppAccountMenuContent.selectCustomer")',
        },

        selectCustomerRoute: {
            type: [Object, String],
            default: null,
        },

        selectAllLocationsLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.selectAllLocations")',
        },

        isSwitchCustomerPage: {
            type: Boolean,
            default: false,
        },

        locations: {
            type: Array,
            default: () => [],
        },

        locationNumber: {
            type: String,
            default: '',
        },

        locationsLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.locations")',
        },

        myCrvLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.myCrv")',
        },

        faqLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.faq")',
        },

        logoutLabel: {
            type: String,
            default: '$t("crvAppAccountMenuContent.logout")',
        },

        marketCountryCode: {
            type: String,
            required: true,
        },

        showMyCrvAndFaq: {
            type: Boolean,
            default: true,
        },

        isMyCrv: {
            type: Boolean,
            default: false,
        },

        mobileMenuMode: {
            type: Boolean,
            default: false,
            note: `Shows a list-item instead of a button when in mobile menu mode`,
        },

        tooltip: {
            type: String,
            default: 'Account menu',
        },

        tooltipCustomerSwitcher: {
            type: String,
            default: 'Switch customer',
        },

        beforeNavigateToCustomerSwitcher: {
            type: Function,
            required: false,
            default: undefined,
        },

        customerNameOverride: {
            type: String,
            required: false,
            default: undefined,
            note: `Overrides the subtitle in the menu activator, use sparingly`
        }
    },

    emits: [
        'logout', 
        'switch-customer', 
        'click-location'
    ],

    data() {
        return {
            showMenu: false,
            showTooltip: false,
            mdiMenuDown,
            mdiAccountCircle,
        }
    },

    computed: {
        formattedCustomerName() {
            if (this.customerNameOverride !== undefined && this.customerNameOverride !== '') {
                return this.customerNameOverride;
            }
            
            if (this.isSwitchCustomerPage) {
                const authorizedCustomers = storageHelper.loadAuthorizedCustomers()
                const customerNumber = storageHelper.loadCustomerNumber()
                if (authorizedCustomers?.includes(customerNumber)) {
                    const name = storageHelper.loadCustomerName()
                    const location = storageHelper.loadLocationNumber()
                    return this.deriveFormattedCustomerName(location, name)
                }
                return this.selectCustomerText
            }

            const locationLabel = this.locations.find(loc => loc.id === this.locationNumber)?.label
            return this.deriveFormattedCustomerName(locationLabel, this.customerName)
        },
    },

    watch: {
        showMenu() {
            if (this.showMenu) {
                this.showTooltip = false
            }
        },
    },

    methods: {
        getColor(isHovering) {
            return isHovering ? 'white' : 'base-brand-blue'            
        },

        deriveFormattedCustomerName(location, customerName) {
            const formattedCustomerName = `${this.customerNumber} ${customerName}`
            if (location) {
                return `${formattedCustomerName}, ${location}`
            }
            return formattedCustomerName
        },

        handleLogout() {
            this.$emit('logout')
        },

        handleSwitchCustomer() {
            this.handleCloseAccountMenu()
            this.$emit('switch-customer')
        },

        handleClickLocation(location) {
            this.$emit('click-location', location)
        },

        handleCloseAccountMenu() {
            this.showMenu = false
        },
    },
}
</script>


<style lang="scss" scoped>
@use '@v3c/assets/styles/variables.scss' as *;

    .is-clickable {
        cursor: pointer;
    }

    .account-name {
        font-weight: 600;
        text-transform: none;
    }

</style>